/**
 * SquadBanner — single squad row in the SquadBannerStack.
 *
 * Per spec §8 + cycle-2 UX F2/F3/F4. Status glyph mirrors the backend
 * phase_events glyph vocabulary (⏳ running, ✓ done, ⊗ recalled, ⏱
 * timed out, ⊘ budget exceeded). Cost glyph is a dot whose color
 * tracks ratio against per-squad cap.
 */
import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import type { Squad, SquadMember } from '../../types/squads';
import { PATTERN_LABEL } from '../../types/squads';
import { SquadRecallConfirm } from './SquadRecallConfirm';


const STATUS_GLYPH: Record<string, string> = {
  dispatching: '⏳',
  running: '⏳',
  completed: '✓',
  recalled: '⊗',
  timed_out: '⏱',
  cost_capped: '⊘',
};


function formatElapsed(fromIso: string): string {
  const elapsed = Math.max(0,
    Math.floor((Date.now() - new Date(fromIso).getTime()) / 1000));
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}


function CostGlyph({ cost, cap }: { cost: number; cap: number }) {
  const ratio = cap > 0 ? cost / cap : 0;
  const color = ratio < 0.3 ? 'bg-slate-400'
                : ratio < 0.7 ? 'bg-amber-500'
                : 'bg-red-500';
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full ${color}`}
      title={`~$${cost.toFixed(2)} (est.) of $${cap.toFixed(2)} cap`}
    />
  );
}


interface SquadBannerProps {
  squad: Squad;
  members: SquadMember[];
  perSquadCostCap?: number;
}


export const SquadBanner: React.FC<SquadBannerProps> = ({
  squad, members, perSquadCostCap = 2.0,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [tick, setTick] = useState(0);  // forces elapsed re-render

  const isRunning = squad.status === 'dispatching' || squad.status === 'running';
  const isTerminal = !isRunning;

  // Tick once per second while squad is running so elapsed updates.
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  const visibleMembers = members.slice(0, 3);
  const memberSummary = visibleMembers.map((m) => m.persona_id).join(', ')
    + (members.length > 3 ? ` +${members.length - 3} more` : '');
  const glyph = STATUS_GLYPH[squad.status] || '·';

  const handleConfirm = async () => {
    try {
      // axios baseURL=/api; do NOT include /api prefix here.
      await api.post(
        `/sessions/${squad.session_id}/squads/${squad.id}/recall`,
        {},
      );
    } catch (err) {
      console.warn('[SquadBanner] recall failed:', err);
    }
    setConfirming(false);
  };

  return (
    <section
      aria-live="polite"
      className="bg-slate-50 dark:bg-slate-800 border border-slate-200
                 dark:border-slate-700 rounded text-slate-700 dark:text-slate-200
                 text-xs font-mono shrink-0"
    >
      <div className="flex items-center gap-2 px-2 py-1">
        <span data-glyph aria-label={`Squad ${squad.status}`}>{glyph}</span>
        <span>Squad · {memberSummary || '(no members)'}</span>
        {isRunning && (
          <>
            <span className="text-slate-500">·</span>
            <CostGlyph
              cost={squad.total_estimated_cost_usd}
              cap={perSquadCostCap}
            />
            <span className="text-slate-500">·</span>
            <span className="tabular-nums" data-elapsed-tick={tick}>
              {formatElapsed(squad.dispatched_at)} elapsed
            </span>
          </>
        )}
        {isTerminal && (
          <>
            <span className="text-slate-500">·</span>
            <span data-actual-cost>
              ${squad.total_actual_cost_usd.toFixed(2)}
            </span>
          </>
        )}
        <div className="flex-1" />
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          aria-label="Toggle expand"
        >
          {expanded ? '▲' : '▼'}
        </button>
        {isRunning && !confirming && (
          <button
            onClick={() => setConfirming(true)}
            className="bg-rose-500/10 hover:bg-rose-500/20 text-rose-700
                       dark:text-rose-300 px-2 py-0.5 rounded text-xs"
          >
            Recall
          </button>
        )}
      </div>

      {confirming && (
        <SquadRecallConfirm
          squad={squad}
          members={members}
          onConfirm={handleConfirm}
          onCancel={() => setConfirming(false)}
        />
      )}

      {expanded && (
        <div className="border-t border-slate-200 dark:border-slate-700 px-2 py-1">
          <div>
            Dispatched by: <strong>{squad.dispatched_by_persona_id}</strong>
          </div>
          <div>Rationale: "{squad.rationale}"</div>
          <div>Source nodes: {squad.source_node_ids?.length ?? 0}</div>
          <div className="mt-1 space-y-0.5">
            {members.map((m) => (
              <div key={m.id} className="flex items-center gap-2">
                <span aria-label={`Member ${m.status}`}>
                  {STATUS_GLYPH[m.status] || '·'}
                </span>
                <span>{m.persona_id}</span>
                <span className="text-slate-500">
                  ({PATTERN_LABEL[m.pattern]}, {m.stance})
                </span>
                <span data-actual-cost>
                  ${m.actual_cost_usd.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
